import type { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { shelfLoaderWhy, type ShelfLoaderWhy } from "@CommonUtils/pog/dividerPolicy";
import { hasShelfLoader, usableShelfDepth } from "@CommonUtils/pog/shelfLoader";
import { translate } from "src/i18n/translate";
import { DEFAULT_LENGTH_UNIT, formatLength } from "src/utils/lengthUnit";

/** Pusher paddle thickness drawn in 3D, pog x100 units (0.1 in, about 3 mm). */
export const DRAWN_PUSHER_PLATE_DEPTH = 10;

/** The hair a pusher paddle is narrower than its facing, pog x100 units, so neighbours never touch. */
export const PUSHER_PLATE_SIDE_GAP = 2;

/** Pusher paddles: steel grey, drawn unlit. */
export const PUSHER_PLATE_COLOR = "#8a8a8a";

/** Height of the loader track slab on the shelf board, pog x100 units (0.25 in). */
export const DRAWN_LOADER_TRACK_HEIGHT = 25;

/** The loader track: steel grey, drawn unlit, so it stands out on the near-black shelf board. */
export const LOADER_TRACK_COLOR = "#9a9a9a";

/** The track's top face, a little lighter so the slab reads as a raised strip from above. */
export const LOADER_TRACK_TOP_COLOR = "#b4b4b4";

/**
 * The spring loader's track: a flat slab over the whole unusable back band of a loader shelf
 * (full shelf width, z from 0, the back, to the loader depth), lying on the shelf board. Null
 * without a loader.
 */
export const getShelfLoaderTrack = (shelf: PlanogramShelf) => {
  if (!hasShelfLoader(shelf)) return null;
  return {
    x: shelf.getXLeft(),
    y: shelf.getYBottom() + shelf.thickness,
    z: 0,
    width: shelf.width,
    height: DRAWN_LOADER_TRACK_HEIGHT,
    depth: Math.min(shelf.depth, shelf.depth - usableShelfDepth(shelf)),
  };
};

export interface ShelfLoaderRow {
  label: string;
  value: string;
}

const counts = ({ needing, facings }: { needing: number; facings: number }) => ({
  needing,
  facings,
});

/**
 * `shelfLoaderWhy` in words, lengths in the merchant's unit. "depth-not-set" has no line in the
 * design review, so it borrows the O line's words.
 */
export const formatShelfLoaderWhy = (why: ShelfLoaderWhy, unit: string): string => {
  switch (why.code) {
    case "needed":
      return why.typicalDepth
        ? translate("common", "shelfLoader.why.neededTypicalDepth", {
            ...counts(why),
            depth: formatLength(why.typicalDepth, unit),
          })
        : translate("common", "shelfLoader.why.needed", counts(why));
    case "needs-more-than-half":
      return translate("common", "shelfLoader.why.needsMoreThanHalf", counts(why));
    case "needed-without-loader":
      return translate("common", "shelfLoader.why.neededWithoutLoader", counts(why));
    case "not-needed":
      return translate("common", "shelfLoader.why.notNeeded");
    case "loader-without-need":
      return translate("common", "shelfLoader.why.loaderWithoutNeed");
    case "set-none-on-fixtures-page":
      return translate("common", "shelfLoader.why.setNoneOnFixturesPage", counts(why));
    case "set-on-fixtures-page":
      return translate("common", "shelfLoader.why.setOnFixturesPage", {
        depth: formatLength(why.depth, unit),
      });
    case "depth-not-set":
      return translate("common", "shelfLoader.why.depthNotSet", counts(why));
    case "on-base-shelf":
      return translate("common", "shelfLoader.why.onBaseShelf", {
        ...counts(why),
        depth: formatLength(why.depth, unit),
      });
    case "not-on-base-shelf":
      return translate("common", "shelfLoader.why.notOnBaseShelf", counts(why));
  }
};

/** Why the shelf has a spring loader or not; empty for pogs generated without a loader rule. */
export const shelfLoaderReason = (shelf: PlanogramShelf, unit: string): string => {
  const why = shelfLoaderWhy({
    loaderDepth: shelf.loaderDepth,
    loaderDecision: shelf.loaderDecision,
    items: shelf.items.map((item) => ({
      columns: item.getHorizontalFacings(),
      loaderNeed: item.loaderNeed,
    })),
  });
  return why ? formatShelfLoaderWhy(why, unit) : "";
};

/**
 * The shelf's spring loader as read-only panel rows after the divider rows
 * (review B.4): its depth or "None" (", set on the Fixtures page" when the
 * store's fixture row decided), the depth left for stock when there is a
 * loader, and why.
 */
export const shelfLoaderRows = (
  shelf: PlanogramShelf,
  unit: string = DEFAULT_LENGTH_UNIT,
): ShelfLoaderRow[] => {
  const reason = shelf.loaderDecision?.reason;
  const depth = formatLength(shelf.loaderDepth ?? 0, unit);
  const label = translate("common", "shelfLoader.rows.label");
  const rows: ShelfLoaderRow[] = hasShelfLoader(shelf)
    ? [
        {
          label,
          value: translate(
            "common",
            reason === "set-on-fixtures-page"
              ? "shelfLoader.rows.depthOnFixturesPage"
              : "shelfLoader.rows.depth",
            { depth },
          ),
        },
        {
          label: translate("common", "shelfLoader.rows.usableDepth"),
          value: formatLength(usableShelfDepth(shelf), unit),
        },
      ]
    : [
        {
          label,
          value: translate(
            "common",
            reason === "set-none-on-fixtures-page"
              ? "shelfLoader.rows.noneOnFixturesPage"
              : "shelfLoader.rows.none",
          ),
        },
      ];
  const why = shelfLoaderReason(shelf, unit);
  return why
    ? [...rows, { label: translate("common", "shelfDividers.rows.why"), value: why }]
    : rows;
};

/**
 * Where an item's stock sits in depth: front-aligned (z runs from the back of
 * the shelf, 0, to its front, `shelf.depth`), `unitsDeep` boxes deep, with the
 * empty space behind it. On a loader shelf that space holds the loader.
 */
export const getItemDepthPlacement = (item: PlanogramItem) => {
  const unitDepth = item.getDepth();
  const unitsDeep = Math.max(0, item.getDepthFacings());
  const stockDepth = unitsDeep * unitDepth;
  const usableDepth = usableShelfDepth(item.shelf);
  const minimumBackZ =
    item.shelf.maxFrontOverhang && unitDepth > usableDepth ? item.shelf.depth - usableDepth : 0;
  const backZ = Math.max(minimumBackZ, item.shelf.depth - stockDepth);
  return { unitsDeep, unitDepth, backZ };
};

export interface PusherPlate {
  x: number;
  y: number;
  z: number;
  width: number;
  height: number;
}

/**
 * One thin pusher plate per facing column of every item on a loader shelf, flat against the
 * back of the column's last unit (z runs from the shelf back, 0, to its front; the stock is
 * front-aligned, so its back is at `backZ`, the usable depth from the front when the stock fills
 * it). A hair narrower than the facing, as tall as one unit or the stock, whichever is lower. No
 * track is drawn here (see getShelfLoaderTrack). Empty for a shelf without a loader.
 */
export const getShelfPusherPlates = (shelf: PlanogramShelf): PusherPlate[] => {
  if (!hasShelfLoader(shelf)) return [];
  const y = shelf.getYBottom() + shelf.thickness;
  return shelf.items.flatMap((item) => {
    const { backZ } = getItemDepthPlacement(item);
    const layout = item.getLayout();
    const z = Math.max(0, backZ - DRAWN_PUSHER_PLATE_DEPTH);
    const facingWidth = item.getWidth();
    const unitHeight = item.getHeight();
    const stockHeight = unitHeight * item.getVerticalFacings() + item.getYCappingHeight();
    const height = Math.max(1, Math.min(unitHeight, stockHeight));
    const width = Math.max(1, facingWidth - PUSHER_PLATE_SIDE_GAP);
    return layout.columnXs.map((x) => ({ x: x + (facingWidth - width) / 2, y, z, width, height }));
  });
};
